import {
    HiArrowPath,
    HiChatBubbleLeft,
    HiCheckCircle,
    HiEnvelope,
    HiInformationCircle,
    HiOutlineCpuChip,
    HiPaperClip,
    HiPencilSquare,
    HiPhone,
    HiShieldCheck,
    HiUserGroup,
} from "react-icons/hi2";

export const buildResourceUrl = (candidateId, path) =>
    `${process.env.REACT_APP_BASE_URL || '/mlapi'}/Candidate/${candidateId}/attachments/file?name=${encodeURIComponent(path)}`;

export const getTimelineIcon = (kind) => {
    const k = (kind || '').toLowerCase();
    if (k === 'note') return <HiChatBubbleLeft />;
    if (k === 'statuschange') return <HiArrowPath />;
    if (k === 'file' || k === 'attachment') return <HiPaperClip />;
    if (k === 'aicompleted') return <HiCheckCircle />;
    if (k === 'aicontact' || k === 'initialized') return <HiOutlineCpuChip />;
    if (k === 'email') return <HiEnvelope />;
    if (k === 'created') return <HiCheckCircle />;
    if (k === 'phonecall') return <HiPhone />;
    if (k === 'meeting') return <HiUserGroup />;
    if (k === 'info') return <HiInformationCircle />;
    if (k === 'verification') return <HiShieldCheck />;
    if (k === 'manualentry') return <HiPencilSquare />;
    if (k === 'aidisclosuresent') return <HiOutlineCpuChip />;
    return <HiArrowPath />;
};

export const getTimelineBadgeProps = (kind, t) => {
    const k = (kind || '').toLowerCase();
    if (k === 'note') return { label: t("candidateDetailPage.timelineKindNote"), colorPalette: "gray" };
    if (k === 'statuschange') return { label: t("candidateDetailPage.timelineKindStatusChange"), colorPalette: "orange" };
    if (k === 'file') return { label: t("candidateDetailPage.timelineKindFile"), colorPalette: "blue" };
    if (k === 'aicompleted') return { label: t("candidateDetailPage.timelineKindAiCompleted"), colorPalette: "green" };
    if (k === 'aicontact') return { label: t("candidateDetailPage.timelineKindAiContact"), colorPalette: "purple" };
    if (k === 'email') return { label: t("candidateDetailPage.timelineKindEmail"), colorPalette: "teal" };
    if (k === 'attachment') return { label: t("candidateDetailPage.timelineKindAttachment"), colorPalette: "cyan" };
    if (k === 'initialized') return { label: t("candidateDetailPage.timelineKindInitialized"), colorPalette: "gray" };
    if (k === 'created') return { label: t("candidateDetailPage.timelineKindCreated"), colorPalette: "gray" };
    if (k === 'phonecall') return { label: t("candidateDetailPage.timelineKindPhoneCall"), colorPalette: "blue" };
    if (k === 'meeting') return { label: t("candidateDetailPage.timelineKindMeeting"), colorPalette: "purple" };
    if (k === 'info') return { label: t("candidateDetailPage.timelineKindInfo"), colorPalette: "gray" };
    if (k === 'verification') return { label: t("candidateDetailPage.timelineKindVerification"), colorPalette: "green" };
    if (k === 'manualentry') return { label: t("candidateDetailPage.timelineKindManual"), colorPalette: "yellow" };
    if (k === 'aidisclosuresent') return { label: t("candidateDetailPage.timelineKindAiDisclosure"), colorPalette: "purple" };
    return { label: kind, colorPalette: "gray" };
};

export const parseMetadata = (metadata) => {
    if (!metadata) return null;
    if (typeof metadata === 'object') return metadata;
    try { return JSON.parse(metadata); } catch { return null; }
};

// Legacy events have no `axis`.
const isAxisChange = (meta) => ['process', 'aiRecommendation', 'humanDecision'].includes(meta?.axis);

export const formatMetadataDisplay = (event, t) => {
    const meta = parseMetadata(event.metadata);
    if (!meta) return null;
    const k = (event.kind || '').toLowerCase();
    if (k === 'note') return null;
    if (k === 'info' && meta.decisionType) {
        const status = meta.status
            ? t(`candidateStatus.${meta.status.toLowerCase()}`)
            : t("candidateDecision.type.reanalyze");
        const main = `${t("candidateDecision.timelineEntry")}: ${t(`candidateDecision.type.${meta.decisionType.toLowerCase()}`)} → ${status}`;
        return meta.differsFromAi ? `${main} · ${t("candidateDecision.differsFromAi")}` : main;
    }
    if (k === 'aidisclosuresent') return `${meta.channel} · ${meta.version} · ${meta.status}`;
    if (k === 'statuschange') {
        const from = meta.oldStatus ?? meta.OldStatus;
        const to = meta.newStatus ?? meta.NewStatus;
        if (isAxisChange(meta) && from && to) {
            return t("candidateAxes.timelineChange", {
                axis: t(`candidateAxes.title.${meta.axis}`),
                from: t(`candidateAxes.${meta.axis}.${from.toLowerCase()}`),
                to: t(`candidateAxes.${meta.axis}.${to.toLowerCase()}`),
            });
        }
        if (from && to) {
            const main = `${t(`candidateStatus.${from.toLowerCase()}`)} → ${t(`candidateStatus.${to.toLowerCase()}`)}`;
            const pFrom = meta.oldProcessStatus, pTo = meta.newProcessStatus;
            if (!pFrom || !pTo || pFrom === pTo) return main;
            return `${main} · ${t("candidateAxes.title.process")}: ${t(`candidateAxes.process.${pFrom.toLowerCase()}`)} → ${t(`candidateAxes.process.${pTo.toLowerCase()}`)}`;
        }
    }
    if (k === 'file' || k === 'attachment') {
        const name = meta.Name ?? meta.name;
        const kind = meta.Kind ?? meta.kind;
        if (name) return kind ? `${name} (${kind})` : name;
    }
    if (k === 'aicompleted') {
        const score = meta.score ?? meta.Score;
        if (score !== undefined) return `Wynik: ${score} pkt`;
    }
    if (k === 'email') {
        const title = meta.title ?? meta.Title;
        if (title) return `${t("candidateDetailPage.timelineDescriptionTitle")}: "${title}"`;
    }
    if (meta.description) return meta.description;
    return JSON.stringify(meta);
};

export const getTimelineEventDescription = (event, t) => {
    const meta = parseMetadata(event.metadata);
    const k = (event.kind || '').toLowerCase();
    if (k === 'statuschange' && isAxisChange(meta)) {
        const to = meta.newStatus ?? meta.NewStatus;
        return t("candidateAxes.timelineChangeDescription", {
            axis: t(`candidateAxes.title.${meta.axis}`),
            value: t(`candidateAxes.${meta.axis}.${to.toLowerCase()}`),
        });
    }
    if (k === 'statuschange') {
        const to = meta?.newStatus ?? meta?.NewStatus;
        const translatedStatus = to ? t(`candidateStatus.${to.toLowerCase()}`) : to;
        const isAuto = (event.authorFullName || '').toLowerCase().includes('system');
        return isAuto
            ? t("candidateDetailPage.timelineDescriptionStatusChangeAuto", { status: translatedStatus })
            : t("candidateDetailPage.timelineDescriptionStatusChange", { status: translatedStatus });
    }
    if (k === 'note') return meta?.Content ?? meta?.content ?? null;
    if (k === 'file' || k === 'attachment') return t("candidateDetailPage.timelineDescriptionFile");
    if (k === 'chatfinished') {
        const score = meta?.score ?? meta?.Score;
        return t("candidateDetailPage.timelineDescriptionAiCompleted", { score: score ?? '?' });
    }
    if (k === 'aicontact') return t("candidateDetailPage.timelineDescriptionAiContact");
    if (k === 'initialized') return t("candidateDetailPage.timelineDescriptionInitialized");
    if (k === 'email') return t("candidateDetailPage.timelineDescriptionEmail");
    if (k === 'created') return t("candidateDetailPage.timelineDescriptionCreated");
    if (k === 'aidisclosuresent') return t("candidateDetailPage.timelineDescriptionAiDisclosure");
    return null;
};
